import { createContext, useContext, useState, useCallback, useMemo, useEffect } from "react";
import { TRANSLATIONS, CATEGORY_LABELS, CYCLE_LABELS } from "@/i18n/translations";
import { setLocale } from "@/lib/format";

const I18nContext = createContext(null);
const STORAGE_KEY = "subzero_lang";
const DEFAULT_LANG = "pl"; // Polish is the default language
const SUPPORTED_LANGS = ["pl", "en", "vi"];

function getInitialLang() {
  if (typeof window === "undefined") return DEFAULT_LANG;
  const saved = localStorage.getItem(STORAGE_KEY);
  return SUPPORTED_LANGS.includes(saved) ? saved : DEFAULT_LANG;
}

function interpolate(str, vars) {
  if (!vars) return str;
  return str.replace(/\{(\w+)\}/g, (m, k) => (vars[k] != null ? String(vars[k]) : m));
}

export function I18nProvider({ children }) {
  const [lang, setLangState] = useState(() => {
    const initial = getInitialLang();
    setLocale(initial);
    return initial;
  });

  const setLang = useCallback((l) => {
    const next = SUPPORTED_LANGS.includes(l) ? l : DEFAULT_LANG;
    setLangState(next);
    setLocale(next);
    try { localStorage.setItem(STORAGE_KEY, next); } catch {}
    try { document.documentElement.lang = next; } catch {}
  }, []);

  // Keep <html lang> in sync with the selected language (initial load + changes).
  // This prevents browser auto-translation prompts (e.g. Brave/Chrome) when the
  // HTML lang attribute disagrees with the displayed language.
  useEffect(() => {
    try { document.documentElement.lang = lang; } catch {}
  }, [lang]);

  const t = useCallback((key, vars) => {
    const dict = TRANSLATIONS[lang] || TRANSLATIONS.pl;
    const fallback = TRANSLATIONS.en;
    const str = dict[key] != null ? dict[key] : (fallback[key] != null ? fallback[key] : key);
    return interpolate(str, vars);
  }, [lang]);

  const tc = useCallback((category) => {
    const map = CATEGORY_LABELS[lang] || CATEGORY_LABELS.pl;
    return map[category] || category;
  }, [lang]);

  const tcycle = useCallback((cycle) => {
    const map = CYCLE_LABELS[lang] || CYCLE_LABELS.pl;
    return map[cycle] || cycle;
  }, [lang]);

  const value = useMemo(() => ({ lang, setLang, t, tc, tcycle }), [lang, setLang, t, tc, tcycle]);

  return <I18nContext.Provider value={value}>{children}</I18nContext.Provider>;
}

export function useI18n() {
  const ctx = useContext(I18nContext);
  if (!ctx) {
    // Safe fallback so components never crash if used outside provider
    return {
      lang: DEFAULT_LANG,
      setLang: () => {},
      t: (k) => k,
      tc: (c) => c,
      tcycle: (c) => c,
    };
  }
  return ctx;
}
